/**
 * Request log — `upstream_call`: every operation we ran against a source,
 * including the ones answered from cache (no HTTP made) and the ones a client
 * pulled through the raw proxy. `origin` says what caused it; `trace` groups the
 * whole fan-out of one inbound request (a search is 1 TecDoc call + 1 Kromeda
 * free-search + N crosses).
 */
import { useState, type ReactNode } from 'react';
import { useApi } from '../lib/api';
import { useDrawer } from '../lib/drawer';
import { useList } from '../lib/hooks';
import type { CallRow, InboundRow, InjectionTrace } from '../lib/types';
import { fmtTime, qs } from '../lib/util';
import {
  Banner,
  DataBlock,
  Empty,
  ErrorBox,
  Loading,
  Pager,
  Seg,
  SourceChip,
  Table,
  Toolbar,
  cx,
} from '../ui';

const LIMIT = 50;

const CACHE_CLASS: Record<string, string> = {
  hit: 'border-ok text-ok',
  miss: 'border-line2 text-muted',
  bypass: 'border-warn text-warn',
  store: 'border-line2 text-muted',
  nostore: 'border-line2 text-faint',
};

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <select className="input" style={{ width: 'auto' }} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}

const ORIGINS: [string, string][] = [
  ['', 'Any origin'],
  ['search', 'search'],
  ['identify', 'identify'],
  ['cross', 'cross'],
  ['vehicles', 'vehicles'],
  ['passthrough', 'proxy'],
  ['console', 'console'],
  ['worker', 'worker'],
];

function Pre({ value }: { value: unknown }): ReactNode {
  return (
    <pre className="mono overflow-auto rounded border border-line bg-panel2 p-3 text-[12px]">
      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

/** Upstream calls. `source` fixes the panel to one database; 'all' shows every source. */
function UpstreamCalls({
  source,
  initialTrace = '',
}: {
  source: 'kromeda' | 'tecdoc' | 'all';
  initialTrace?: string;
}) {
  const { request } = useApi();
  const { show } = useDrawer();
  const [origin, setOrigin] = useState('');
  const [outcome, setOutcome] = useState('');
  const [cache, setCache] = useState('');
  const [trace, setTrace] = useState(initialTrace);
  const [offset, setOffset] = useState(0);

  const fixed = source === 'all' ? undefined : source;
  const path = `/v1/admin/requests${qs({ source: fixed, origin, outcome, cache, trace, limit: LIMIT, offset })}`;
  const { data, loading, error } = useList<CallRow>(path);

  const reset = (fn: () => void) => {
    fn();
    setOffset(0);
  };

  async function detail(id: number) {
    const r = await request('GET', `/v1/admin/requests/${id}`);
    const row = r.data as (CallRow & { reqParams?: unknown; response?: { rawBody?: unknown } | null }) | undefined;
    show({
      title: `Call #${id} — ${row?.source ?? ''} ${row?.op ?? ''}`,
      body: (
        <div className="flex flex-col gap-3">
          <div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-faint">Request</div>
            <Pre value={{ method: row?.method, url: row?.url, params: row?.reqParams }} />
          </div>
          <div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-faint">
              Cached response {row?.rawResponseId ? `(raw_response #${row.rawResponseId})` : '(not cached)'}
            </div>
            <Pre value={row?.response?.rawBody ?? '— this call was not cached —'} />
          </div>
          <button className="btn btn-sm self-start" onClick={() => reset(() => setTrace(String(row?.traceId ?? '')))}>
            Show the whole trace
          </button>
        </div>
      ),
    });
  }

  const items = data?.items ?? [];

  return (
    <>
      <Toolbar>
        <Select value={origin} onChange={(v) => reset(() => setOrigin(v))} options={ORIGINS} />
        <Select
          value={outcome}
          onChange={(v) => reset(() => setOutcome(v))}
          options={[
            ['', 'Any outcome'],
            ['ok', 'ok'],
            ['error', 'errors only'],
          ]}
        />
        <Select
          value={cache}
          onChange={(v) => reset(() => setCache(v))}
          options={[
            ['', 'Any cache'],
            ['hit', 'cache hit'],
            ['miss', 'went to the wire'],
            ['bypass', 'forced refresh'],
            ['nostore', 'not cached'],
          ]}
        />
        <input
          className="input mono"
          style={{ flex: 1, minWidth: 160 }}
          placeholder="Trace id…"
          value={trace}
          onChange={(e) => reset(() => setTrace(e.target.value))}
        />
        {trace && (
          <button className="btn btn-sm" onClick={() => reset(() => setTrace(''))}>
            Clear trace
          </button>
        )}
      </Toolbar>
      {error && <ErrorBox>{error}</ErrorBox>}
      <DataBlock
        title="Upstream calls"
        sub={data ? `${data.total.toLocaleString()} calls` : undefined}
        raw={data}
        right={data ? <Pager total={data.total} limit={LIMIT} offset={offset} onOffset={setOffset} /> : undefined}
      >
        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty>No calls logged yet.</Empty>
        ) : (
          <Table cols={['When', 'Source', 'Operation', 'Origin', 'Status', 'ms', 'Cache', 'Result', '']}>
            {items.map((r) => (
              <tr key={r.id}>
                <td className="mono num">{fmtTime(r.createdAt, true)}</td>
                <td>
                  <SourceChip source={r.source} />
                </td>
                <td className="mono">{r.op}</td>
                <td className="text-muted">{r.origin}</td>
                <td className={!r.ok || (r.httpStatus ?? 0) >= 400 ? 'text-bad' : undefined}>{r.httpStatus ?? '—'}</td>
                <td className="num">{r.latencyMs ?? '—'}</td>
                <td>
                  <span className={cx('chip', CACHE_CLASS[r.cache] ?? 'border-line2 text-muted')}>{r.cache}</span>
                </td>
                <td className={r.ok ? 'text-muted' : 'text-bad'}>{r.ok ? 'ok' : (r.errorMessage ?? 'error')}</td>
                <td>
                  <button className="btn btn-sm" onClick={() => detail(r.id)}>
                    Detail
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </DataBlock>
    </>
  );
}

/**
 * What our catalog did to a request. The case worth surfacing is the amber one —
 * matched, but added nothing — because "we found the product and the upstream
 * already had it" is the answer to a surprised "the client only got one row".
 */
function Injected({ trace }: { trace: InjectionTrace | null }) {
  if (!trace || trace.matched === 0) return <span className="text-faint">—</span>;
  const { injected, enriched, matched, productIds, source, op } = trace;
  const changed = injected + enriched;
  const label = changed === 0 ? `0 / ${matched}` : [injected ? `+${injected}` : '', enriched ? `~${enriched}` : ''].filter(Boolean).join(' ');
  return (
    <span
      className={cx('chip', changed === 0 ? 'border-warn text-warn' : 'border-ok text-ok')}
      title={`${source}/${op} — matched ${matched}, injected ${injected}, enriched ${enriched}\nproducts: ${productIds.join(', ')}`}
    >
      {label}
    </span>
  );
}

/** Inbound requests we served, each linking to the upstream calls it caused. */
function InboundRequests({ onTrace }: { onTrace: (t: string) => void }) {
  const [offset, setOffset] = useState(0);
  const { data, loading, error } = useList<InboundRow>(
    `/v1/admin/requests/inbound${qs({ limit: LIMIT, offset })}`,
  );
  const items = data?.items ?? [];

  return (
    <>
      {error && <ErrorBox>{error}</ErrorBox>}
      <DataBlock
        title="Inbound requests"
        sub={data ? `${data.total.toLocaleString()} requests` : undefined}
        raw={data}
        right={data ? <Pager total={data.total} limit={LIMIT} offset={offset} onOffset={setOffset} /> : undefined}
      >
        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty>Nothing served yet.</Empty>
        ) : (
          <Table cols={['When', 'Method', 'Path', 'Key', 'Status', 'ms', 'Upstream', 'Injected', '']}>
            {items.map((r) => (
              <tr key={r.id}>
                <td className="mono num">{fmtTime(r.createdAt, true)}</td>
                <td className="mono">{r.method}</td>
                <td className="mono">{r.path}</td>
                <td className="mono text-faint">{r.keyHint ?? '—'}</td>
                <td className={(r.status ?? 0) >= 400 ? 'text-bad' : undefined}>{r.status ?? '—'}</td>
                <td className="num">{r.latencyMs ?? '—'}</td>
                <td className="num">{r.upstreamCalls}</td>
                <td><Injected trace={r.injection} /></td>
                <td>
                  <button className="btn btn-sm" disabled={r.upstreamCalls === 0} onClick={() => onTrace(r.traceId)}>
                    Fan-out
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </DataBlock>
    </>
  );
}

export default function RequestLog({ source = 'all' }: { source?: 'kromeda' | 'tecdoc' | 'all' }) {
  const [tab, setTab] = useState<'upstream' | 'inbound'>('upstream');
  // Remounting UpstreamCalls with a key is how "Fan-out" jumps to a trace.
  const [trace, setTrace] = useState('');

  const jump = (t: string) => {
    setTrace(t);
    setTab('upstream');
  };

  return (
    <>
      <Banner icon="⇄">
        Every call to a source is logged here — search fan-out, raw proxy traffic and console pokes alike — including
        the ones answered from cache, which make no upstream request at all.
      </Banner>
      {source === 'all' && (
        <Toolbar>
          <Seg
            value={tab}
            onChange={setTab}
            options={[
              { value: 'upstream', label: 'Upstream calls' },
              { value: 'inbound', label: 'Inbound requests' },
            ]}
          />
        </Toolbar>
      )}
      {tab === 'upstream' || source !== 'all' ? (
        <UpstreamCalls key={trace} source={source} initialTrace={trace} />
      ) : (
        <InboundRequests onTrace={jump} />
      )}
    </>
  );
}
